<!-- eslint-disable no-undef -->
<script setup>
    // Imports
    import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
    import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';

    // Deffinitions
    const emit = defineEmits(['RowsSelected', 'ActionSelected']);

    const props = defineProps({
        title: {
            type: String,
            default: ''
        },
        data: {
            type: Array,
            required: true
        },
        actions: {
            type: Array,
            default: () => []
        },
        actionException: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        selectable: {
            type: Boolean,
            default: false
        },
        preSelect: {
            type: Array,
            default: () => []
        },
        pagination: {
            type: Boolean,
            default: false
        },
        rowsPerPage: {
            type: Number,
            default: 10
        },
        pageSizeOptions: {
            type: Array,
            default: () => [5, 10, 25, 50, 100]
        },
        showId: {
            type: Boolean,
            default: true
        },
        responsive: {
            type: Boolean,
            default: false
        }
    });

    // References
    const columns = ref([]);
    const selectedRows = ref(props.preSelect);
    const currentPage = ref(1);
    const pageSize = ref(props.rowsPerPage);
    const openDropdownIndex = ref(null);
    const dropdownPosition = ref({ top: 0, left: 0 });

    // Watch functions
    watch(() => props.data, (newData) => {
        if (Array.isArray(newData) && newData.length > 0 && newData[0]) {
            columns.value = Object.keys(newData[0]);
        } else {
            columns.value = [];
        }
        // Only reset selected rows if there are no preselected rows
        if (props.preSelect.length === 0) {
            selectedRows.value = [];
        }
        currentPage.value = 1;
    }, { immediate: true, deep: true });

    // Add watch for preSelect prop
    watch(() => props.preSelect, (newPreSelect) => {
        selectedRows.value = [...newPreSelect];
    }, { immediate: true });

    watch(selectedRows, () => {
        // Emit the selected rows
        emit('RowsSelected', selectedRows.value);
    }, { deep: true });

    watch(pageSize, () => {
        // Reset to first page when changing page size
        currentPage.value = 1;
    });


    // Computed properties
    const allSelected = computed(() => {
        return displayedData.value.length > 0 && 
               selectedRows.value.length === props.data.length;
    });

    const someSelected = computed(() => {
        return selectedRows.value.length > 0 && 
               selectedRows.value.length < props.data.length;
    });

    const totalPages = computed(() => {
        return Math.ceil(props.data.length / pageSize.value);
    });

    const paginatedData = computed(() => {
        const start = (currentPage.value - 1) * pageSize.value;
        const end = start + pageSize.value;
        return props.data.slice(start, end);
    });
    
    const displayedData = computed(() => {
        return props.pagination ? paginatedData.value : props.data;
    });

    const filteredColumns = computed(() => {
        if (!props.showId) {
            return columns.value.filter(column => column.toLowerCase() !== 'id');
        }
        return columns.value;
    });

    const hasAnyRowWithActions = computed(() => {
        if (props.actions.length === 0) {
            return false;
        }
        return props.data.some(row => shouldShowActionsForRow(row));
    });

    // Create a reactive key based on data changes to force re-render when columns change
    const tableKey = computed(() => {
        return `${props.data.length}-${columns.value.join('-')}`;
    });


    // Methods
    const shouldShowActionsForRow = (row) => {
        // If no exceptions are defined, show actions for all rows
        if (props.actionException.length === 0) {
            return true;
        }
        // Check if the row's id is in the exception list
        return !props.actionException.includes(row.id);
    };

    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return '--';
        }
        return value;
    }

    const formatColumnTitle = (columnName) => {
        return columnName
            .split(/[\s_-]+/) // Split on spaces, underscores, or hyphens
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    const toggleRowSelection = (index) => {
        const position = selectedRows.value.indexOf(index);
        if (position === -1) {
            selectedRows.value.push(index);
        } else {
            selectedRows.value.splice(position, 1);
        }
    }

    const toggleSelectAll = () => {
        if (allSelected.value) {
            selectedRows.value = [];
        } else {
            selectedRows.value = props.data.map((_, index) => index);
        }
    }

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages.value) {
            currentPage.value = page;
        }
    };

    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            currentPage.value++;
        }
    };

    const prevPage = () => {
        if (currentPage.value > 1) {
            currentPage.value--;
        }
    };

    const changePageSize = (size) => {
        pageSize.value = size;
    };

    const toggleDropdown = (index, event) => {
        if (openDropdownIndex.value === index) {
            openDropdownIndex.value = null;
        } else {
            openDropdownIndex.value = index;
            // Calculate dropdown position
            const button = event.currentTarget;
            const rect = button.getBoundingClientRect();
            const dropdownHeight = props.actions.length * 40 + 8; // Approximate height (40px per item + padding)
            const viewportHeight = window.innerHeight;
            
            // Check if there's enough space below, otherwise position above
            const spaceBelow = viewportHeight - rect.bottom;
            const shouldOpenUpward = spaceBelow < dropdownHeight + 20;
            
            dropdownPosition.value = {
                top: shouldOpenUpward ? rect.top - dropdownHeight : rect.bottom + 4,
                left: rect.right - 192 // 192px = w-48 (12rem = 192px)
            };
        }
    };

    const closeDropdown = () => {
        openDropdownIndex.value = null;
    };

    const handleActionClick = (action, row, rowIndex) => {
        const actualRowIndex = props.pagination ? ((currentPage.value - 1) * pageSize.value) + rowIndex : rowIndex;
        emit('ActionSelected', {
            action: action,
            row: row,
            rowIndex: actualRowIndex
        });
        closeDropdown();
    };

    // Click outside handler
    const handleClickOutside = (event) => {
        // Check if the click is not on a dropdown button or inside a dropdown
        if (!event.target.closest('button[aria-haspopup="true"]') &&
            !event.target.closest('[data-generic-table-dropdown]')) {
            closeDropdown();
        }
    };

    // Setup click outside listener
    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
    });

</script>

<template>
    <div class="relative flex flex-col">
        <!-- Loading overlay -->
        <div v-if="loading" class="absolute inset-0 bg-mc-cloud/80 dark:bg-mc-charcoal/80 flex items-center justify-center z-10 rounded-mc-md">
            <LoadingAnimation3 class="w-8 h-8" size="40px" />
        </div>

        <!-- Optional title -->
        <h2 v-if="title" class="font-display font-bold text-tier-1 uppercase tracking-widest text-ink-muted dark:text-on-dark-muted px-4 pt-3 pb-1">
            {{ title }}
        </h2>

        <!-- Table -->

        <!-- Mobile card list: only visible below md when responsive=true -->
        <div
            v-if="responsive"
            data-card-list
            class="md:hidden space-y-3 p-3"
        >
            <div
                v-for="(row, rowIndex) in displayedData"
                :key="`card-${rowIndex}-${row.id}`"
                data-card-item
                class="ui-card p-3"
            >
                <!-- Primary title + subtitle -->
                <div class="flex items-start justify-between gap-2 mb-2">
                    <div class="min-w-0 flex-1">
                        <div class="font-semibold text-sm text-mc-ink dark:text-on-dark truncate">
                            {{ formatValue(row[filteredColumns[0]]) }}
                        </div>
                        <div v-if="filteredColumns[1]" class="text-xs text-ink-muted dark:text-on-dark-muted mt-0.5 truncate">
                            {{ formatValue(row[filteredColumns[1]]) }}
                        </div>
                    </div>
                    <!-- Badge columns (index 2+) -->
                    <div class="flex flex-wrap gap-1 flex-shrink-0">
                        <span
                            v-for="col in filteredColumns.slice(2)"
                            :key="`badge-${rowIndex}-${col}`"
                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-mc-steel/20 dark:bg-white/10 text-mc-ink dark:text-on-dark"
                        >
                            {{ formatValue(row[col]) }}
                        </span>
                    </div>
                </div>
                <!-- Action buttons -->
                <div
                    v-if="actions.length > 0 && shouldShowActionsForRow(row)"
                    class="flex gap-2 pt-2 border-t border-mc-steel/30 dark:border-line-on-dark mt-2"
                >
                    <button
                        v-for="action in actions"
                        :key="`card-action-${rowIndex}-${action}`"
                        data-card-action
                        type="button"
                        @click="handleActionClick(action, row, rowIndex)"
                        class="flex-1 py-1.5 text-xs font-semibold rounded border transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                        :class="['delete', 'Delete', 'remove', 'Remove'].includes(action)
                            ? 'border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                            : 'border-mc-blue text-mc-blue hover:bg-mc-blue/10'"
                    >
                        {{ action }}
                    </button>
                </div>
                <div
                    v-else-if="actions.length > 0 && !shouldShowActionsForRow(row)"
                    class="pt-2 border-t border-mc-steel/30 dark:border-line-on-dark mt-2"
                >
                    <p class="text-center text-xs text-ink-muted dark:text-on-dark-muted italic">Can't edit yourself</p>
                </div>
            </div>
            <div v-if="displayedData.length === 0" class="px-4 py-8 text-center text-sm text-ink-muted dark:text-on-dark-muted">
                No data available
            </div>
        </div>

        <!-- Desktop table (always shown when responsive=false; hidden on mobile when responsive=true) -->
        <div :class="responsive ? 'hidden md:block overflow-x-auto' : 'overflow-x-auto'">
            <div v-if="props.data.length > 0 && columns.length > 0" class="inline-block min-w-full align-middle">
                <table :key="tableKey" class="w-full border-collapse whitespace-nowrap">
                    <thead class="sticky top-0">
                        <tr>
                            <th v-if="selectable" class="bg-mc-ink dark:bg-black/50 px-3 py-2.5 text-left w-10">
                                <input
                                    v-if="displayedData.length > 0"
                                    type="checkbox"
                                    :checked="allSelected"
                                    :indeterminate="someSelected"
                                    @change="toggleSelectAll"
                                    class="w-4 h-4 cursor-pointer touch-manipulation accent-mc-red"
                                />
                            </th>
                            <th
                                v-for="column in filteredColumns"
                                :key="`header-${column}`"
                                class="bg-mc-ink dark:bg-black/50 px-3 py-2.5 text-left font-mono text-xs uppercase tracking-[0.08em] text-on-dark whitespace-nowrap min-w-[100px]"
                            >
                                {{ formatColumnTitle(column) }}
                            </th>
                            <th
                                v-if="hasAnyRowWithActions"
                                class="bg-mc-ink dark:bg-black/50 px-3 py-2.5 w-12"
                            ></th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-mc-steel/40 dark:divide-line-on-dark">
                        <tr
                            v-for="(row, rowIndex) in displayedData"
                            :key="`${pagination ? ((currentPage - 1) * pageSize) + rowIndex : rowIndex}-${Object.values(row).join('-')}`"
                            class="transition-colors duration-120 hover:bg-mc-blue/5 dark:hover:bg-white/5"
                        >
                            <td v-if="selectable" class="px-3 py-2.5 w-10">
                                <input
                                    type="checkbox"
                                    :checked="selectedRows.includes(pagination ? ((currentPage - 1) * pageSize) + rowIndex : rowIndex)"
                                    @change="toggleRowSelection(pagination ? ((currentPage - 1) * pageSize) + rowIndex : rowIndex)"
                                    class="w-4 h-4 cursor-pointer touch-manipulation accent-mc-red"
                                />
                            </td>
                            <td
                                v-for="column in filteredColumns"
                                :key="`cell-${rowIndex}-${column}`"
                                class="px-3 py-2.5 text-left text-sm font-body text-mc-ink dark:text-on-dark min-w-[100px] max-w-xs"
                            >
                                <div class="truncate" :title="String(formatValue(row[column]))">
                                    {{ formatValue(row[column]) }}
                                </div>
                            </td>
                            <td v-if="hasAnyRowWithActions && shouldShowActionsForRow(row)" class="px-3 py-2.5 w-12">
                                <button
                                    @click="toggleDropdown(rowIndex, $event)"
                                    class="w-7 h-7 flex items-center justify-center rounded-mc-sm hover:bg-mc-steel/50 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 transition-colors touch-manipulation"
                                    :aria-expanded="openDropdownIndex === rowIndex"
                                    aria-haspopup="true"
                                >
                                    <svg class="w-3.5 h-3.5 text-ink-muted dark:text-on-dark-muted" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                    </svg>
                                </button>
                            </td>
                            <td v-else-if="hasAnyRowWithActions && !shouldShowActionsForRow(row)" class="px-3 py-2.5 w-12"></td>
                        </tr>
                        <tr v-if="displayedData.length === 0">
                            <td
                                :colspan="(selectable ? 1 : 0) + filteredColumns.length + (hasAnyRowWithActions ? 1 : 0)"
                                class="px-4 py-8 text-center font-body text-sm text-ink-muted dark:text-on-dark-muted"
                            >
                                No data available
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else-if="!loading && props.data.length === 0" class="px-4 py-8 text-center font-body text-sm text-ink-muted dark:text-on-dark-muted">
                No data available
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination" class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 px-4 py-3 border-t border-mc-steel/50 dark:border-line-on-dark">
            <div class="flex items-center gap-2">
                <span class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted">Rows</span>
                <select
                    v-model="pageSize"
                    class="input-base !py-1 !px-2 !text-xs !w-auto min-w-[60px] touch-manipulation"
                    @change="changePageSize(parseInt($event.target.value))"
                >
                    <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                </select>
            </div>

            <span class="font-mono text-xs text-ink-muted dark:text-on-dark-muted">
                {{ props.data.length ? ((currentPage - 1) * pageSize) + 1 : 0 }}–{{ Math.min(currentPage * pageSize, props.data.length) }} of {{ props.data.length }}
            </span>

            <div class="flex items-center gap-1">
                <button
                    type="button"
                    @click="prevPage"
                    :disabled="currentPage === 1"
                    class="btn-secondary !px-3 !py-1 !text-xs touch-manipulation"
                >
                    ‹ Prev
                </button>

                <button
                    type="button"
                    v-for="page in Math.min(5, totalPages)"
                    :key="page"
                    @click="goToPage(page)"
                    class="w-8 h-8 flex items-center justify-center rounded-mc-sm text-xs font-semibold font-body transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    :class="currentPage === page
                        ? 'bg-mc-red text-white'
                        : 'text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/40 dark:hover:bg-white/10'"
                >
                    {{ page }}
                </button>

                <span v-if="totalPages > 5 && currentPage < totalPages - 2" class="px-1 font-mono text-xs text-ink-muted dark:text-on-dark-muted">…</span>

                <button
                    type="button"
                    v-if="totalPages > 5 && currentPage < totalPages - 1"
                    @click="goToPage(totalPages)"
                    class="w-8 h-8 flex items-center justify-center rounded-mc-sm text-xs font-semibold font-body transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    :class="currentPage === totalPages
                        ? 'bg-mc-red text-white'
                        : 'text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/40 dark:hover:bg-white/10'"
                >
                    {{ totalPages }}
                </button>

                <button
                    type="button"
                    @click="nextPage"
                    :disabled="currentPage === totalPages || totalPages === 0"
                    class="btn-secondary !px-3 !py-1 !text-xs touch-manipulation"
                >
                    Next ›
                </button>
            </div>
        </div>
    </div>

    <!-- Dropdown teleported to body to avoid clipping -->
    <Teleport to="body">
        <div
            v-if="openDropdownIndex !== null"
            data-generic-table-dropdown
            class="fixed w-44 bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md shadow-mc-md z-[9999]"
            :style="{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }"
            @click.stop
        >
            <div class="py-1 flex flex-col">
                <button
                    v-for="(action, actionIndex) in actions"
                    :key="`action-${actionIndex}`"
                    @click="handleActionClick(action, displayedData[openDropdownIndex], openDropdownIndex)"
                    class="w-full text-left px-4 py-2 text-sm font-body text-mc-ink dark:text-on-dark hover:bg-mc-cloud dark:hover:bg-white/10 focus-visible:outline-none focus-visible:bg-mc-cloud dark:focus-visible:bg-white/10 transition-colors touch-manipulation"
                >
                    {{ action }}
                </button>
            </div>
        </div>
    </Teleport>
</template>
